// react
import React, { useRef, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useForm, useFieldArray } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
// UI
import { message, Row, Col } from "antd";
import { CardElement } from "../../../../components/Common/UIElements";

import {
  ButtonPrimary,
  Button,
} from "../../../../components/Common/Button/index";
import PropertyApprover from "../../../../components/Onboarding/PropertyApprover/PropertyApprover";

// processing
import {
  PROPERTY_APPROVAL_OPPERATIONS,
  ONBOARDING_PROPERTY_STATUS,
} from "../../../../utils/onboarding-helper";

import { onboardingPropertyApproveValidateSchema } from "../../../../schema/onboarding-properties.schema";
import { useGetCustomer } from "../../../../hooks/Onboarding/onboarding.hooks";

import {
  useApproveProperty,
  useRequestValidationProperty,
  useRemoveProperties,
  useGetPropertiesByCustomer,
  useAddProperty,
} from "../../../../hooks/Onboarding/onboarding-properties.hooks";

const PropertyContainer = (props) => {
  const { operation } = props;

  const { id } = useParams();
  const customerId = id;
  const history = useHistory();
  const [numberOfProperties, setNumberOfProperties] = useState(0);

  // ref for submitting individual forms in workflow
  const approvePropertyFormRef = useRef();
  const { mutateAsync: addProperty } = useAddProperty({
    onSuccess: () => {
      message.success("Property details Updated successfully");
    },
    onError: (error) => {
      message.error(error.response?.data?.message ?? "Server Error");
    },
  });
  // fetch customer (HMG)
  const { data: apiCustomerData } = useGetCustomer(customerId, {
    enabled: !!customerId,
    onSuccess: (data) => {
      setNumberOfProperties(data.number_of_properties);
    },
    onError: (error) => {
      message.error(error.response?.data?.message ?? "Server Error");
    },
  });
  // fetch properties
  const { data: apiPropertyData } = useGetPropertiesByCustomer(customerId, {
    enabled: !!customerId,
    select: (data) => {
      const updatedData = [...data];
      return updatedData.map((e) => {
        return {
          ...e,
          property_contact: {
            _id: e?.property_contact_id?._id,
            first_name: e?.property_contact_id?.first_name,
            last_name: e?.property_contact_id?.last_name,
            email: e?.property_contact_id?.email,
          },
        };
      });
    },
  });

  const {
    handleSubmit,
    control,
    formState,
    setValue,
    reset,
    getValues,
    trigger,
    clearErrors,
    watch,
  } = useForm({
    mode: "onChange",
    resolver: yupResolver(onboardingPropertyApproveValidateSchema),
    context: {
      maxOnboardingProperties: numberOfProperties,
    },
  });

  const { fields, remove, append } = useFieldArray({
    control,
    name: "propertyData",
  });

  useEffect(() => {
    if (apiPropertyData) {
      // set property data
      reset({ propertyData: apiPropertyData });
    }
  }, [apiPropertyData]);

  const getCurrentSelection = (getSelectedIndex) => {
    const tempFields = getValues("propertyData");
    const selection = getSelectedIndex
      ? tempFields.reduce((acc, field, index) => {
          if (field.property_selected == true) {
            acc.push(index);
          }
          return acc;
        }, [])
      : tempFields.filter((field) => {
          return field.property_selected === true;
        });
    return selection;
  };

  // remove fileds that are not in new selection
  const removeOtherSelections = (newSelections) => {
    const newSelectionIds = newSelections.map((selection) => selection.id);
    const fieldSelection = [...getValues("propertyData")];
    for (let x = 0; x < fieldSelection.length; x++) {
      const field = fieldSelection[x];
      if (!newSelectionIds.includes(field.id)) {
        field.property_selected = false;
      } else {
        field.property_selected = true;
      }
    }
    setValue("propertyData", [...fieldSelection]);
  };

  const removeSelectedFromFieldArray = () => {
    const selected = getCurrentSelection(true);
    remove(selected);
  };

  // approve property
  const { mutateAsync: approvePropertyList } = useApproveProperty({
    onSuccess: () => {
      removeSelectedFromFieldArray();
      message.success("Property(ies) successfully approved");
    },
    onError: (error) => {
      message.error(error.response?.data?.message ?? "Server Error");
    },
  });

  // remove property
  const { mutate: removePropertyList } = useRemoveProperties({
    onSuccess: () => {
      removeSelectedFromFieldArray();
      message.success("Properties successfully removed");
    },
    onError: (error) => {
      message.error(error.response?.data?.message ?? "Server Error");
    },
  });

  // request property validation
  const { mutate: validatePropertyList } = useRequestValidationProperty({
    onSuccess: () => {
      removeSelectedFromFieldArray();
      message.success("Validation request successfully sent");
    },
    onError: (error) => {
      message.error(error.response?.data?.message ?? "Server Error");
    },
  });

  // approve all remaining properties
  const propertyDetailsApproveSubmit = async (formData) => {
    const approveStatus = await approvePropertyList({
      customerId,
      propertyList: formData.propertyData,
    });
    // TODO:need to add approve all function
    if (approveStatus?.success) {
      history.push("/onboarding/dashboard");
    }
  };

  const performPropertyOperation = async (operationType, id, index) => {
    try {
      // clear exising field errors
      clearErrors();
      let propertyList = [];
      // get property selection if not passed
      if (index === undefined) {
        propertyList = getCurrentSelection();
      } else {
        propertyList = [getValues(`propertyData.${index}`)];
        // removeOtherSelections(propertyList);
      }

      // at least 1 property
      if (propertyList.length === 0) {
        message.error("Please select at least one property");
        return;
      }
      // trigger selected field validation
      const selectedIndexes = getCurrentSelection(true);
      let validFields = true;
      if (operationType !== PROPERTY_APPROVAL_OPPERATIONS.REMOVE) {
        for (const index of selectedIndexes) {
          setValue(`propertyData.${index}.operationType`, operationType);
          const validationResult = await trigger(`propertyData[${index}]`);
          if (!validationResult) {
            validFields = false;
          }
        }
      }

      // all valid
      if (validFields) {
        const formattedProperties = propertyList.map((property) => {
          const formattedProperty = { ...property };
          delete formattedProperty.id;
          delete formattedProperty.property_selected;
          formattedProperty.status =
            ONBOARDING_PROPERTY_STATUS.PENDING_APPROVAL;
          formattedProperty.customer_id = customerId;
          formattedProperty.property_contact.status =
            PROPERTY_APPROVAL_OPPERATIONS.APPROVE === operationType
              ? ONBOARDING_PROPERTY_STATUS.APPROVED
              : ONBOARDING_PROPERTY_STATUS.PENDING_APPROVAL;

          return formattedProperty;
        });
        if (PROPERTY_APPROVAL_OPPERATIONS.APPROVE === operationType) {
          approvePropertyList({
            customerId,
            propertyList: formattedProperties,
          });
        }
        if (PROPERTY_APPROVAL_OPPERATIONS.VALIDATE === operationType) {
          validatePropertyList({
            customerId,
            propertyList: formattedProperties,
          });
        }
        if (PROPERTY_APPROVAL_OPPERATIONS.REMOVE === operationType) {
          const removeFields = propertyList.filter((field) => {
            return field._id === id || field.property_selected === true;
          });
          const propertiesToRemove = removeFields
            .filter((idx) => idx._id !== undefined)
            .map((item) => item._id);

          if (propertiesToRemove.length > 0) {
            removePropertyList({ propertyList: propertiesToRemove });
          } else removeSelectedFromFieldArray();
        }
      }
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <CardElement title="System Discovery">
      <Row>
        <Col>
          <div className="card-header">Create your Property List</div>
          <div className="card-sub-header">
            Your properties have already been entered. Use "Add Property" option
            if anything is missing and then click approve properties later on
            your dashboard.
          </div>
        </Col>
      </Row>
      <Row>
        <Col span={24}>
          <PropertyApprover
            formSubmitRef={approvePropertyFormRef}
            onSubmit={handleSubmit(propertyDetailsApproveSubmit)}
            operation={operation}
            control={control}
            errors={formState.errors}
            allowedProperties={numberOfProperties}
            setValue={setValue}
            getValues={getValues}
            fields={fields}
            remove={remove}
            append={append}
            customerId={customerId}
            performPropertyOperation={performPropertyOperation}
            watch={watch}
          />
        </Col>
      </Row>
      <Row>
        <Col span={16} offset={4}>
          <div style={{ textAlign: "center" }}>
            <Button onClick={() => history.push("/onboarding/dashboard")}>
              Go to Dashboard
            </Button>
            <ButtonPrimary
              onClick={() => approvePropertyFormRef.current.click()}
            >
              Approve Properties
            </ButtonPrimary>
          </div>
        </Col>
      </Row>
    </CardElement>
  );
};

export default PropertyContainer;
